import React, { useState, useEffect } from 'react';
import { User } from '../types/user';
import { ImportModal } from '../components/user/ImportModal';
import { UserView } from '../components/user/UserView';
import EditUser from '../components/user/EditUser';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/ui/use-toast';
import { Upload, Eye, Edit, X } from 'lucide-react';
import { userService } from '../services/userService';
import { Spinner } from "../components/ui/Spinner";
import { useUser } from '../contexts/UserContext';
import ImportResultsModal from '../components/user/ImportResultsModal';
import { Input } from '../components/ui/input';
import { ExportExcel } from '../components/ui/ExportExcel';


export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportMappingModalOpen, setIsImportMappingModalOpen] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useUser();
  const [importResults, setImportResults] = useState<any[] | null>(null);
  const [filterUsername, setFilterUsername] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Applied filters - these are sent to API
  const [appliedFilters, setAppliedFilters] = useState({ username: '', name: '', role: '', status: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (currentUser) {
      setIsInitialLoad(true);
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && !isInitialLoad) {
      fetchUsersForPage(currentPage);
    }
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchUsers = async (filters = appliedFilters) => {
    if (!currentUser) return;
    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1);
      setUsers([]);

      const identifier = currentUser.role === 'corporate_admin'
        ? currentUser.username
        : currentUser.role === 'distributor_admin'
          ? currentUser.distributorCode
          : currentUser.username;
      const response = await userService.getAllUsers(identifier, currentUser.role, 1, pageSize, filters);

      setUsers(response.data);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError('Failed to fetch users. Please try again later.');
      toast({
        title: "Error",
        description: "Failed to fetch users. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersForPage = async (page: number) => {
    if (!currentUser) return;
    try {
      setLoading(true);
      setError(null);

      const identifier = currentUser.role === 'corporate_admin'
        ? currentUser.username
        : currentUser.role === 'distributor_admin'
          ? currentUser.distributorCode
          : currentUser.username;
      const response = await userService.getAllUsers(identifier, currentUser.role, page, pageSize, appliedFilters);

      setUsers(response.data);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError('Failed to fetch users. Please try again later.');
      toast({
        title: "Error",
        description: "Failed to fetch users. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
    }
  };

  const handleEditUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setEditingUser(user);
    }
  };

  const handleUserUpdated = (updatedUser: User) => {
    fetchUsers();
  };

  const handleImportSuccess = (response: any) => {
    setImportResults(response.results || []);
    toast({
      title: "Success",
      description: response.message || "Users imported successfully",
    });
    fetchUsers();
  };

  const handleImportMappingSuccess = (response: any) => {
    setImportResults(response.results || []);
    toast({
      title: "Success",
      description: response.message || "Trainer-Seller mappings imported successfully",
    });
  };

  // Apply filters - sends filter values to API and resets to page 1
  const handleApplyFilters = () => {
    const newFilters = {
      username: filterUsername,
      name: filterName,
      role: filterRole,
      status: filterStatus
    };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchUsers(newFilters);
  };

  if (selectedUser) {
    return (
      <UserView
        user={selectedUser}
        onBack={() => setSelectedUser(null)}
      />
    );
  }

  if (editingUser) {
    return (
      <EditUser
        user={editingUser}
        onBack={() => setEditingUser(null)}
        onUserUpdated={handleUserUpdated}
      />
    );
  }

  const getStatusColor = (status: User['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <Spinner label="Loading users..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-red-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>User Management</CardTitle>
          <div className="flex gap-2">
            <Button
              onClick={() => setIsImportModalOpen(true)}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Users
            </Button>
            <Button
              onClick={() => setIsImportMappingModalOpen(true)}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Trainer-Seller Mapping
            </Button>
            <ExportExcel
              data={users}
              columns={[
                { label: 'Username', key: 'username' },
                { label: 'Name', key: 'name' },
                { label: 'Role', key: 'role' },
                { label: 'Status', key: 'status' },
              ]}
              filename="users.xlsx"
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters and Count */}
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium mb-1">Username</label>
              <Input
                value={filterUsername}
                onChange={e => setFilterUsername(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleApplyFilters()}
                placeholder="Filter by username"
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Name</label>
              <Input
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleApplyFilters()}
                placeholder="Filter by name"
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Role</label>
              <select
                value={filterRole}
                onChange={e => setFilterRole(e.target.value)}
                className="w-32 border rounded h-9 px-2 text-sm"
              >
                <option value="">All</option>
                <option value="trainer">Trainer</option>
                <option value="seller">Seller</option>
                <option value="distributor_admin">Distributor_Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="w-32 border rounded h-9 px-2 text-sm"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <Button
              className="h-9"
              onClick={handleApplyFilters}
              disabled={loading}
            >
              Apply Filters
            </Button>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => {
                setFilterUsername('');
                setFilterName('');
                setFilterRole('');
                setFilterStatus('');
                const emptyFilters = { username: '', name: '', role: '', status: '' };
                setAppliedFilters(emptyFilters);
                setCurrentPage(1);
                fetchUsers(emptyFilters);
              }}
              disabled={
                !filterUsername && !filterName && !filterRole && !filterStatus
              }
            >
              <X className="mr-2 h-4 w-4" />
              Clear Filters
            </Button>
            <div className="ml-auto text-sm font-medium">
              {(() => {
                const startRecord = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
                const endRecord = Math.min(currentPage * pageSize, totalCount);
                return totalCount > 0 
                  ? `Showing ${startRecord} to ${endRecord} of ${totalCount} total records`
                  : 'No records found';
              })()}
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell className="capitalize">{user.role}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(user.status)}>
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1).toLowerCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewUser(user.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(user.id)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-6 gap-2">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
              >
                Previous
              </Button>
              <span>Page {currentPage} of {totalPages}</span>
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || loading}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleImportSuccess}
        onImport={(users) => userService.importUsers(users, currentUser?.username ?? '', currentUser?.role ?? '')}
        title="Import Users"
        sampleTemplateUrl="/import-sample-excel/ImportusersSampleFile.xlsx"
        parseRow={(row) => ({
          Username: row.Username !== undefined && row.Username !== null && row.Username !== '' ? String(row.Username) : '',
          Name: row.Name !== undefined && row.Name !== null && row.Name !== '' ? String(row.Name) : '',
          Role: row.Role !== undefined && row.Role !== null && row.Role !== '' ? String(row.Role) : '',
          SellerType: row.SellerType !== undefined && row.SellerType !== null && row.SellerType !== '' ? String(row.SellerType) : '',
          Location: row.Location !== undefined && row.Location !== null && row.Location !== '' ? String(row.Location) : '',
          CurrentBelt: row.CurrentBelt !== undefined && row.CurrentBelt !== null && row.CurrentBelt !== '' ? String(row.CurrentBelt) : '',
          TotalPoints: row.TotalPoints !== undefined && row.TotalPoints !== null && row.TotalPoints !== '' ? row.TotalPoints : 0,
          DistributorCode: row.DistributorCode !== undefined && row.DistributorCode !== null && row.DistributorCode !== '' ? String(row.DistributorCode) : '',
          TrainerCode: row.TrainerCode !== undefined && row.TrainerCode !== null && row.TrainerCode !== '' ? String(row.TrainerCode) : '',
        })}
      />
      
      <ImportModal
        isOpen={isImportMappingModalOpen}
        onClose={() => setIsImportMappingModalOpen(false)}
        onSuccess={handleImportMappingSuccess}
        onImport={(mappings: any[]) => userService.importTrainerSellerMapping(mappings)}
        title="Import Trainer-Seller Mapping"
        sampleTemplateUrl="/import-sample-excel/ImportTrainerSellerMapping.xlsx"
        parseRow={(row: any) => ({
          SellerCode: row.SellerCode !== undefined && row.SellerCode !== null && row.SellerCode !== '' ? String(row.SellerCode) : '',
          TrainerCode: row.TrainerCode !== undefined && row.TrainerCode !== null && row.TrainerCode !== '' ? String(row.TrainerCode) : '',
        })}
      />
      
      {importResults && (
        <ImportResultsModal
          results={importResults}
          onClose={() => setImportResults(null)}
      />
      )}
    </div>
  );
}; 